import { SignalBase } from "./SignalBase";
import { SignalConnection } from "./SignalConnection";

//
// Flambe - Rapid game development
// https://github.com/aduros/flambe/blob/master/LICENSE.txt

/**
 * An alias for Signal2 listeners.
 */
export interface Listener2<A, B> {
  (arg1: A, arg2: B): void;
}

/**
 * A two-argument signal. See Signal0 and Signal1 for different arities.
 */
export class Signal2<A, B> extends SignalBase {
  /**
   * @param listener An optional listener to immediately connect to the signal.
   */
  constructor(listener?: Listener2<A, B>) {
    super(listener);
  }

  /**
   * Connects a listener to this signal.
   * @param prioritize True if this listener should fire before others.
   * @returns A SignalConnection, that can be disposed to remove the listener.
   */
  public connect(
    listener: Listener2<A, B>,
    prioritize: boolean = false
  ): SignalConnection {
    return this.connectImpl(listener, prioritize);
  }

  /**
   * Emit the signal, notifying each connected listener.
   */
  public emit(arg1: A, arg2: B) {
    if (this.dispatching()) {
      this.defer(() => {
        this.emitImpl(arg1, arg2);
      });
    } else {
      this.emitImpl(arg1, arg2);
    }
  }

  private emitImpl(arg1: A, arg2: B) {
    let head = this.willEmit();
    let p = head;
    while (p != null) {
      p._listener(arg1, arg2);
      if (!p.stayInList) {
        p.dispose();
      }
      p = p._next as SignalConnection;
    }
    this.didEmit(head);
  }
}
